import { Modal, message } from 'antd'
import { useEffect, useRef } from 'react'

const SCRIPT_ID = 'onlyoffice-api-script'

const normalizeServerUrl = (url = '') => url.replace(/\/+$/, '')

const loadOnlyOfficeScript = (documentServerUrl) =>
  new Promise((resolve, reject) => {
    const serverUrl = normalizeServerUrl(documentServerUrl)

    if (!serverUrl) {
      reject(new Error('OnlyOffice server URL is not configured'))
      return
    }

    if (window.DocsAPI?.DocEditor) {
      resolve(window.DocsAPI)
      return
    }

    const existing = document.getElementById(SCRIPT_ID)

    if (existing) {
      const onReady = () => {
        if (window.DocsAPI?.DocEditor) {
          resolve(window.DocsAPI)
        } else {
          reject(new Error('OnlyOffice API failed to initialize'))
        }
      }

      if (window.DocsAPI?.DocEditor) {
        onReady()
        return
      }

      existing.addEventListener('load', onReady)
      existing.addEventListener('error', () => reject(new Error('OnlyOffice script failed to load')))
      return
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = `${serverUrl}/web-apps/apps/api/documents/api.js`
    script.async = true
    script.onload = () => {
      if (window.DocsAPI?.DocEditor) {
        resolve(window.DocsAPI)
      } else {
        reject(new Error('OnlyOffice API failed to initialize'))
      }
    }
    script.onerror = () =>
      reject(
        new Error(
          `Could not load OnlyOffice from ${serverUrl}. Check ONLYOFFICE_URL, HTTPS, and that Document Server is running.`
        )
      )
    document.body.appendChild(script)
  })

export default function OnlyOfficeEditorModal({ open, onClose, editConfig }) {
  const containerRef = useRef(null)
  const editorRef = useRef(null)

  useEffect(() => {
    if (!open || !editConfig?.documentServerUrl || !editConfig?.config) {
      return undefined
    }

    let cancelled = false

    const destroyEditor = () => {
      try {
        editorRef.current?.destroyEditor?.()
      } catch {
        // ignore destroy errors
      }

      editorRef.current = null

      if (containerRef.current) {
        containerRef.current.innerHTML = ''
      }
    }

    const initEditor = async () => {
      try {
        const DocsAPI = await loadOnlyOfficeScript(editConfig.documentServerUrl)

        if (cancelled || !containerRef.current) {
          return
        }

        destroyEditor()

        const placeId = 'onlyoffice-editor-host'
        containerRef.current.id = placeId

        editorRef.current = new DocsAPI.DocEditor(placeId, {
          ...editConfig.config,
          events: {
            onError: (event) => {
              console.error('[OnlyOffice]', event)
              message.error(event?.data || 'OnlyOffice editor error')
            }
          }
        })
      } catch (err) {
        if (!cancelled) {
          message.error(
            err?.message ||
              'OnlyOffice editor could not load. Verify ONLYOFFICE_URL points to a running Document Server.'
          )
          onClose?.()
        }
      }
    }

    const timer = window.setTimeout(initEditor, 300)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
      destroyEditor()
    }
  }, [open, editConfig, onClose])

  return (
    <Modal
      title={editConfig?.canEdit === false ? 'View document' : 'Edit document'}
      open={open}
      onCancel={onClose}
      footer={null}
      width="90vw"
      destroyOnClose
      afterOpenChange={(isOpen) => {
        if (!isOpen && containerRef.current) {
          containerRef.current.innerHTML = ''
        }
      }}
      styles={{ body: { height: '75vh', padding: 0 } }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%', minHeight: '70vh' }} />
    </Modal>
  )
}
